"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KicadBusTopology = exports.KicadBusExpressionType = exports.KicadBusDriverType = void 0;
var KicadBusDriverType;
(function (KicadBusDriverType) {
    KicadBusDriverType["LocalLabel"] = "local_bus_label";
    KicadBusDriverType["HierarchicalLabel"] = "hierarchical_bus_label";
    KicadBusDriverType["SheetPin"] = "bus_sheet_pin";
})(KicadBusDriverType || (exports.KicadBusDriverType = KicadBusDriverType = {}));
var KicadBusExpressionType;
(function (KicadBusExpressionType) {
    KicadBusExpressionType["Group"] = "group";
    KicadBusExpressionType["Vector"] = "vector";
})(KicadBusExpressionType || (exports.KicadBusExpressionType = KicadBusExpressionType = {}));
class KicadBusTopology {
    constructor() {
        this.tracks = [];
        this.junctions = [];
        this.drivers = [];
    }
    static isBusLabel(text) {
        return KicadBusTopology.parseBusLabel(text) !== undefined;
    }
    static parseBusLabel(text) {
        const vector = text.match(/^([^\s[\]]*)\[(-?\d+)\.\.(-?\d+)\]$/);
        if (vector) {
            const first = BigInt(vector[2]);
            const last = BigInt(vector[3]);
            const step = first <= last ? 1n : -1n;
            const members = [];
            for (let member = first;; member += step) {
                members.push(String(member));
                if (member === last) {
                    break;
                }
            }
            return { type: KicadBusExpressionType.Vector, prefix: vector[1], members };
        }
        const group = this.findOuterGroup(text);
        if (!group || /[\s[\]]/.test(group.prefix) || /[~^_]$/.test(group.prefix)) {
            return undefined;
        }
        return { type: KicadBusExpressionType.Group, prefix: group.prefix, members: this.splitMembers(group.contents) };
    }
    addTrack(track) {
        this.tracks.push(track);
    }
    addJunction(position) {
        this.junctions.push(position);
    }
    addDriver(text, type, position, moduleRef, pinRef) {
        this.drivers.push({ text, type, position, moduleRef, pinRef });
    }
    build() {
        const parents = Array.from({ length: this.tracks.length + this.drivers.length }, (_, index) => index);
        for (let first = 0; first < this.tracks.length; first++) {
            for (let second = first + 1; second < this.tracks.length; second++) {
                if (this.shareEndpoint(this.tracks[first], this.tracks[second])) {
                    this.unionNodes(parents, first, second);
                }
            }
        }
        for (const junction of this.junctions) {
            const touching = this.tracks
                .map((track, index) => ({ track, index }))
                .filter(({ track }) => this.isOnTrack(junction, track));
            for (const { index } of touching.slice(1)) {
                this.unionNodes(parents, touching[0].index, index);
            }
        }
        for (let driverIndex = 0; driverIndex < this.drivers.length; driverIndex++) {
            const nodeIndex = this.tracks.length + driverIndex;
            for (let trackIndex = 0; trackIndex < this.tracks.length; trackIndex++) {
                if (this.isOnTrack(this.drivers[driverIndex].position, this.tracks[trackIndex])) {
                    this.unionNodes(parents, nodeIndex, trackIndex);
                }
            }
            for (let previous = 0; previous < driverIndex; previous++) {
                if (this.samePoint(this.drivers[driverIndex].position, this.drivers[previous].position)) {
                    this.unionNodes(parents, nodeIndex, this.tracks.length + previous);
                }
            }
        }
        const componentIds = new Map();
        const components = new Map();
        for (let index = 0; index < this.drivers.length; index++) {
            const driver = this.drivers[index];
            const root = this.findNode(parents, this.tracks.length + index);
            const componentId = componentIds.get(root) ?? `bus-${componentIds.size}`;
            componentIds.set(root, componentId);
            const component = components.get(componentId) ?? { id: componentId, drivers: [] };
            component.drivers.push({
                text: driver.text,
                type: driver.type,
                componentId,
                moduleRef: driver.moduleRef,
                pinRef: driver.pinRef,
            });
            components.set(componentId, component);
        }
        return [...components.values()];
    }
    findNode(parents, index) {
        const parent = parents[index];
        if (parent === index) {
            return index;
        }
        const root = this.findNode(parents, parent);
        parents[index] = root;
        return root;
    }
    unionNodes(parents, first, second) {
        const firstRoot = this.findNode(parents, first);
        const secondRoot = this.findNode(parents, second);
        if (firstRoot !== secondRoot) {
            parents[secondRoot] = firstRoot;
        }
    }
    static splitMembers(contents) {
        const members = [];
        let depth = 0;
        let start = 0;
        for (let index = 0; index <= contents.length; index++) {
            const character = contents[index];
            if (character === '{') {
                depth++;
            }
            else if (character === '}') {
                depth--;
            }
            if ((index === contents.length || /\s/.test(character)) && depth === 0) {
                const member = contents.slice(start, index).trim();
                if (member.length > 0) {
                    members.push(member);
                }
                start = index + 1;
            }
        }
        return members;
    }
    static findOuterGroup(text) {
        for (let open = 0; open < text.length; open++) {
            if (text[open] !== '{') {
                continue;
            }
            let depth = 0;
            for (let close = open; close < text.length; close++) {
                if (text[close] === '{') {
                    depth++;
                }
                else if (text[close] === '}') {
                    depth--;
                    if (depth === 0 && close === text.length - 1) {
                        return { prefix: text.slice(0, open), contents: text.slice(open + 1, close) };
                    }
                }
            }
        }
        return undefined;
    }
    shareEndpoint(first, second) {
        return (this.samePoint(first.start, second.start) ||
            this.samePoint(first.start, second.end) ||
            this.samePoint(first.end, second.start) ||
            this.samePoint(first.end, second.end));
    }
    isOnTrack(point, track) {
        const [x, y] = point;
        const [startX, startY] = track.start;
        const [endX, endY] = track.end;
        const cross = (x - startX) * (endY - startY) - (y - startY) * (endX - startX);
        if (Math.abs(cross) > 0.000001) {
            return false;
        }
        return (x >= Math.min(startX, endX) - 0.000001 &&
            x <= Math.max(startX, endX) + 0.000001 &&
            y >= Math.min(startY, endY) - 0.000001 &&
            y <= Math.max(startY, endY) + 0.000001);
    }
    samePoint(first, second) {
        return Math.abs(first[0] - second[0]) < 0.000001 && Math.abs(first[1] - second[1]) < 0.000001;
    }
}
exports.KicadBusTopology = KicadBusTopology;
//# sourceMappingURL=KicadBusTopology.js.map